import React, { useState } from "react";
import styles from "./Quiz.module.scss";
import {
  EditableQuestion,
  MAX_HINTS,
  QuestionType,
  TYPE_LABELS,
  WHO_AM_I_CATEGORIES,
} from "./types";

interface QuestionEditorProps {
  initial: EditableQuestion;
  isSaving: boolean;
  onCancel: () => void;
  onSave: (question: EditableQuestion) => void;
  onDelete?: () => void;
}

const validate = (q: EditableQuestion): string | null => {
  if (!q.date) return "Velg en dato.";
  if (!q.question.trim()) return "Skriv inn spørsmålet.";
  if (q.options.length < 2) return "Legg til minst to svaralternativer.";
  if (q.options.some((o) => !o.answer.trim())) return "Fyll ut alle svaralternativene.";
  const answers = q.options.map((o) => o.answer.trim().toLowerCase());
  if (new Set(answers).size !== answers.length) return "Svaralternativene må være ulike.";
  if (q.correctIndex === null) return "Marker hvilket svar som er riktig.";
  if (q.type === "WHO_AM_I" && q.hints.filter((h) => h.trim()).length === 0) {
    return "Legg til minst ett hint.";
  }
  return null;
};

const QuestionEditor: React.FC<QuestionEditorProps> = ({
  initial,
  isSaving,
  onCancel,
  onSave,
  onDelete,
}) => {
  const [q, setQ] = useState<EditableQuestion>(initial);
  const [validationError, setValidationError] = useState<string | null>(null);
  const isWhoAmI = q.type === "WHO_AM_I";
  const visibleHints = q.hints.filter((h) => h.trim());
  const isExisting = initial.id !== undefined;

  const update = <K extends keyof EditableQuestion>(field: K, value: EditableQuestion[K]) =>
    setQ((prev) => ({ ...prev, [field]: value }));

  const setType = (type: QuestionType) =>
    setQ((prev) => ({
      ...prev,
      type,
      question: type === "WHO_AM_I" && !prev.question ? "Hvem er jeg?" : prev.question,
      hints: type === "WHO_AM_I" && prev.hints.length === 0 ? ["", "", ""] : prev.hints,
    }));

  const setHint = (index: number, value: string) =>
    update("hints", q.hints.map((h, i) => (i === index ? value : h)));

  const setOption = (index: number, value: string) =>
    update("options", q.options.map((o, i) => (i === index ? { ...o, answer: value } : o)));

  const removeOption = (index: number) =>
    setQ((prev) => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
      correctIndex:
        prev.correctIndex === index
          ? null
          : prev.correctIndex !== null && prev.correctIndex > index
            ? prev.correctIndex - 1
            : prev.correctIndex,
    }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate(q);
    setValidationError(error);
    if (!error) onSave(q);
  };

  return (
    <div className={styles.editorLayout}>
      <form className={styles.panel} onSubmit={handleSubmit}>
        <fieldset className={styles.fieldset} disabled={isExisting}>
          <legend className={styles.legend}>Quiztype</legend>
          <div className={styles.typeGrid}>
            {(Object.keys(TYPE_LABELS) as QuestionType[]).map((type) => (
              <label
                key={type}
                className={`${styles.typeCard} ${q.type === type ? styles.typeCardActive : ""}`}
              >
                <input
                  type="radio"
                  name="type"
                  checked={q.type === type}
                  onChange={() => setType(type)}
                />
                <span>
                  <strong>{TYPE_LABELS[type]}</strong>
                  <small>
                    {type === "WHO_AM_I"
                      ? "Opptil 3 hint, poeng faller per hint"
                      : "Ett spørsmål, flere svar"}
                  </small>
                </span>
              </label>
            ))}
          </div>
          {isExisting && <small className={styles.muted}>Typen kan ikke endres etter at spørsmålet er laget.</small>}
        </fieldset>

        <div className={styles.fieldRow}>
          {isWhoAmI && (
            <label className={styles.field}>
              Kategori
              <select
                value={q.category}
                onChange={(e) => update("category", e.target.value)}
              >
                <option value="">Ingen</option>
                {WHO_AM_I_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className={styles.field}>
            Publiseres (kl. 17:00)
            <input
              type="date"
              value={q.date}
              onChange={(e) => update("date", e.target.value)}
            />
          </label>
        </div>

        <label className={styles.field}>
          {isWhoAmI ? "Overskrift" : "Spørsmål"}
          <textarea
            rows={2}
            value={q.question}
            placeholder={isWhoAmI ? "Hvem er jeg?" : "Skriv spørsmålet her …"}
            onChange={(e) => update("question", e.target.value)}
          />
        </label>

        {isWhoAmI && (
          <fieldset className={styles.fieldset}>
            <legend className={styles.legend}>Hint – fra vanskeligst til lettest</legend>
            {Array.from({ length: MAX_HINTS }, (_, i) => (
              <label key={i} className={styles.hintRow}>
                <span className={styles.hintBadge}>{i + 1}</span>
                <textarea
                  rows={2}
                  aria-label={`Hint ${i + 1}`}
                  value={q.hints[i] ?? ""}
                  placeholder={i === 0 ? "Det vanskeligste hintet vises først" : "Valgfritt"}
                  onChange={(e) => setHint(i, e.target.value)}
                />
              </label>
            ))}
          </fieldset>
        )}

        <fieldset className={styles.fieldset}>
          <legend className={styles.legend}>Svaralternativer – marker det riktige</legend>
          <div className={styles.optionGrid}>
            {q.options.map((option, i) => (
              <label
                key={option.id ?? `new-${i}`}
                className={`${styles.optionField} ${q.correctIndex === i ? styles.optionCorrect : ""}`}
              >
                <input
                  type="radio"
                  name="correct"
                  aria-label={`Alternativ ${i + 1} er riktig`}
                  checked={q.correctIndex === i}
                  onChange={() => update("correctIndex", i)}
                />
                <input
                  type="text"
                  aria-label={`Alternativ ${i + 1}`}
                  value={option.answer}
                  onChange={(e) => setOption(i, e.target.value)}
                />
                {option.id === undefined && q.options.length > 2 && (
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label={`Fjern alternativ ${i + 1}`}
                    onClick={() => removeOption(i)}
                  >
                    ×
                  </button>
                )}
              </label>
            ))}
          </div>
          {q.options.length < 6 && (
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => update("options", [...q.options, { answer: "" }])}
            >
              + Legg til alternativ
            </button>
          )}
        </fieldset>

        <label className={styles.field}>
          «Visste du?» – vises etter svar (valgfritt)
          <textarea
            rows={2}
            value={q.fact}
            onChange={(e) => update("fact", e.target.value)}
          />
        </label>

        {validationError && (
          <p role="alert" className={styles.errorText}>
            {validationError}
          </p>
        )}

        <div className={styles.formActions}>
          {onDelete && (
            <button type="button" className={styles.dangerButton} onClick={onDelete} disabled={isSaving}>
              Slett
            </button>
          )}
          <span className={styles.spacer} />
          <button type="button" className={styles.secondaryButton} onClick={onCancel} disabled={isSaving}>
            Avbryt
          </button>
          <button type="submit" className={styles.primaryButton} disabled={isSaving}>
            {isSaving ? "Lagrer …" : "Lagre spørsmål"}
          </button>
        </div>
      </form>

      <aside className={styles.panel}>
        <strong>Forhåndsvisning</strong>
        <div className={styles.preview}>
          <span className={styles.previewLabel}>
            {[q.category, isWhoAmI ? "30 p mulig" : "10 poeng"].filter(Boolean).join(" · ")}
          </span>
          {isWhoAmI ? (
            <>
              <div className={styles.previewCard}>1 · {visibleHints[0] || "Første hint"}</div>
              {visibleHints.length > 1 && (
                <div className={styles.previewLocked}>
                  {visibleHints.length === 2 ? "Hint 2 låst" : "Hint 2 og 3 låst"}
                </div>
              )}
            </>
          ) : (
            <div className={styles.previewCard}>{q.question || "Spørsmålet vises her"}</div>
          )}
        </div>
        <p className={styles.muted}>
          {isWhoAmI
            ? "Poeng: 30 med 1 hint, 20 med 2, 10 med 3. De raskeste får bonus som før."
            : "Poeng: 10, pluss bonus til de 10 raskeste."}
        </p>
      </aside>
    </div>
  );
};

export default QuestionEditor;
